import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaCalendarAlt,
  FaChartBar,
  FaCheckCircle,
  FaClock,
  FaCopy,
  FaDownload,
  FaEnvelopeOpen,
  FaExclamationTriangle,
  FaFileCsv,
  FaFileExcel,
  FaFilePdf,
  FaInbox,
  FaPaperPlane,
  FaSync,
  FaTimes,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import "./Dashboard.css";

const MANUAL_BASELINE_MIN = 15;

function formatMs(ms) {
  if (ms == null || ms === 0) return "—";
  const totalSec = ms / 1000;
  if (totalSec < 60) {
    return totalSec < 10 ? `${totalSec.toFixed(1)}s` : `${Math.round(totalSec)}s`;
  }
  const m = Math.floor(totalSec / 60);
  const s = Math.round(totalSec % 60);
  return `${m}m ${s}s`;
}

function formatTimeSaved(minutes) {
  const totalMin = parseFloat(minutes) || 0;
  if (totalMin <= 0) return "0s";
  const totalSec = totalMin * 60;
  if (totalSec < 60) return `${Math.round(totalSec)}s`;
  const m = Math.floor(totalMin);
  const s = Math.round((totalMin - m) * 60);
  if (totalMin >= 60) {
    const h = Math.floor(totalMin / 60);
    const rm = Math.round(totalMin % 60);
    return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
  }
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function verdictLabel(cls) {
  return { "pill-danger": "Phishing", "pill-warn": "Suspicious", "pill-ok": "Safe", "pill-neutral": "Unknown" }[cls] ?? "Unknown";
}

function verdictColor(cls) {
  return { "pill-danger": "#ef4444", "pill-warn": "#f97316", "pill-ok": "#22c55e", "pill-neutral": "#64748b" }[cls] ?? "#64748b";
}

function formatShortDate(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatSpan(seconds) {
  const s = Number(seconds) || 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
}

function dominantVerdictClass(arr) {
  const list = Array.isArray(arr) ? arr : [];
  return list.find((c) => c === "pill-danger")
    ?? list.find((c) => c === "pill-warn")
    ?? list.find((c) => c === "pill-ok")
    ?? list[0]
    ?? "pill-neutral";
}

const REPEAT_PAGE_SIZE = 5;
const RECENT_PAGE_SIZE = 5;

const PRESETS = [
  { id: "24h", label: "Last 24h" },
  { id: "7d",  label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
];

const MAX_RANGE_MS = 90 * 24 * 60 * 60 * 1000;

// "YYYY-MM-DDTHH:mm" in *local* time, suitable for <input type="datetime-local">
function toLocalInputValue(date) {
  if (!date) return "";
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Dashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [repeatPage, setRepeatPage] = useState(0);
  const [recentPage, setRecentPage] = useState(0);

  // Filter state
  const [preset, setPreset] = useState("30d");
  const [customOpen, setCustomOpen] = useState(false);
  // Applied (committed) custom range — only set on Apply.
  const [customFrom, setCustomFrom] = useState(() =>
    toLocalInputValue(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
  );
  const [customTo, setCustomTo] = useState(() => toLocalInputValue(new Date()));
  const [customError, setCustomError] = useState("");
  // Refs to the uncontrolled inputs. We read values directly so opening the
  // native datetime picker isn't disrupted by React re-rendering on each keystroke.
  const customFromRef = useRef(null);
  const customToRef = useRef(null);

  // Export menu
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(null); // "pdf" | "xlsx" | "csv" | null
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onClick = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [exportOpen]);

  const handleExport = async (format) => {
    setExportOpen(false);
    setExporting(format);
    try {
      const params = new URLSearchParams({ range: preset, format });
      if (preset === "custom") {
        params.set("from", new Date(customFrom).toISOString());
        params.set("to", new Date(customTo).toISOString());
      }
      const res = await fetch(`/api/reports/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const msg = await res.json().catch(() => ({}));
        throw new Error(msg.error || `HTTP ${res.status}`);
      }
      // Pull filename from Content-Disposition if present
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const name = m ? m[1] : `phishmatrix-report.${format}`;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Export failed");
    } finally {
      setExporting(null);
    }
  };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ range: preset });
      if (preset === "custom") {
        params.set("from", new Date(customFrom).toISOString());
        params.set("to", new Date(customTo).toISOString());
      }
      const res = await fetch(`/api/reports/dashboard?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const msg = await res.json().catch(() => ({}));
        throw new Error(msg.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [token, preset, customFrom, customTo]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { setRepeatPage(0); setRecentPage(0); }, [data]);

  const applyCustomRange = () => {
    const fromValue = customFromRef.current?.value || "";
    const toValue = customToRef.current?.value || "";

    if (!fromValue || !toValue) {
      setCustomError("Both dates are required.");
      return;
    }
    const f = new Date(fromValue);
    const t = new Date(toValue);
    if (isNaN(f.getTime()) || isNaN(t.getTime())) {
      setCustomError("Invalid date format.");
      return;
    }
    if (f >= t) {
      setCustomError("'From' must be earlier than 'To'.");
      return;
    }
    if (t.getTime() - f.getTime() > MAX_RANGE_MS) {
      setCustomError("Range cannot exceed 90 days.");
      return;
    }
    setCustomError("");
    setCustomFrom(fromValue);
    setCustomTo(toValue);
    setPreset("custom");
    setCustomOpen(false);
  };

  const activeRangeLabel = (() => {
    if (preset !== "custom") {
      return PRESETS.find((p) => p.id === preset)?.label || preset;
    }
    const f = new Date(customFrom);
    const t = new Date(customTo);
    const fmt = (d) =>
      d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    return `${fmt(f)} → ${fmt(t)}`;
  })();

  if (loading) {
    return (
      <div className="dash-page">
        <div className="dash-loading">
          <div className="dash-spinner" />
          Loading dashboard…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash-page">
        <div className="dash-error-state">
          <FaExclamationTriangle />
          <p>{error}</p>
          <button className="dash-retry-btn" onClick={fetchDashboard}>Retry</button>
        </div>
      </div>
    );
  }

  const s = data?.summary ?? {};
  const byVerdict = data?.by_verdict ?? [];
  const byDay = data?.by_day ?? [];
  const recent = data?.recent ?? [];
  const repeats = data?.repeats ?? [];
  const topSenders = data?.top_senders ?? [];
  const topRecipients = data?.top_recipients ?? [];

  const maxDay = Math.max(...byDay.map((d) => d.count), 1);
  const totalByVerdict = byVerdict.reduce((a, r) => a + r.count, 0) || 1;
  const maxSenderCount = Math.max(...topSenders.map((r) => r.count), 1);
  const maxRecipientCount = Math.max(...topRecipients.map((r) => r.count), 1);

  const repeatTotalPages = Math.max(1, Math.ceil(repeats.length / REPEAT_PAGE_SIZE));
  const repeatPageSafe = Math.min(repeatPage, repeatTotalPages - 1);
  const repeatsSlice = repeats.slice(repeatPageSafe * REPEAT_PAGE_SIZE, (repeatPageSafe + 1) * REPEAT_PAGE_SIZE);

  const recentTotalPages = Math.max(1, Math.ceil(recent.length / RECENT_PAGE_SIZE));
  const recentPageSafe = Math.min(recentPage, recentTotalPages - 1);
  const recentSlice = recent.slice(recentPageSafe * RECENT_PAGE_SIZE, (recentPageSafe + 1) * RECENT_PAGE_SIZE);

  return (
    <div className="dash-page">
      {/* ── Header ── */}
      <div className="dash-header">
        <div>
          <h1 className="dash-title">Analytics Dashboard</h1>
          <p className="dash-subtitle">
            Phishing investigation metrics &amp; time-savings summary
            <span className="dash-subtitle-range"> · {activeRangeLabel}</span>
          </p>
        </div>

        <div className="dash-header-actions">
          <div className="dash-export-wrap" ref={exportMenuRef}>
            <button
              type="button"
              className="dash-export-btn"
              onClick={() => setExportOpen((o) => !o)}
              disabled={!!exporting}
              title="Export current view"
            >
              <FaDownload />
              <span>{exporting ? `Exporting ${exporting.toUpperCase()}…` : "Export"}</span>
            </button>
            {exportOpen && (
              <div className="dash-export-menu" role="menu">
                <button type="button" className="dash-export-item" onClick={() => handleExport("pdf")}>
                  <FaFilePdf className="export-icon-pdf" />
                  <div className="dash-export-item-body">
                    <div className="dash-export-item-label">PDF</div>
                    <div className="dash-export-item-sub">Formatted report with charts</div>
                  </div>
                </button>
                <button type="button" className="dash-export-item" onClick={() => handleExport("xlsx")}>
                  <FaFileExcel className="export-icon-xlsx" />
                  <div className="dash-export-item-body">
                    <div className="dash-export-item-label">XLSX</div>
                    <div className="dash-export-item-sub">All emails + summary sheets</div>
                  </div>
                </button>
                <button type="button" className="dash-export-item" onClick={() => handleExport("csv")}>
                  <FaFileCsv className="export-icon-csv" />
                  <div className="dash-export-item-body">
                    <div className="dash-export-item-label">CSV</div>
                    <div className="dash-export-item-sub">All emails in range</div>
                  </div>
                </button>
              </div>
            )}
          </div>

          <button className="dash-refresh-btn" onClick={fetchDashboard} title="Refresh">
            <FaSync />
          </button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="dash-filter-bar">
        <div className="dash-filter-chips">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`dash-chip ${preset === p.id ? "dash-chip-active" : ""}`}
              onClick={() => { setPreset(p.id); setCustomOpen(false); }}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className={`dash-chip dash-chip-custom ${preset === "custom" ? "dash-chip-active" : ""}`}
            onClick={() => setCustomOpen((o) => !o)}
            title="Custom date/time range (max 90 days)"
          >
            <FaCalendarAlt /> Custom
          </button>
          {preset === "custom" && (
            <span className="dash-active-range">{activeRangeLabel}</span>
          )}
        </div>

        {customOpen && (
          <div className="dash-custom-panel">
            <div className="dash-custom-row">
              <label className="dash-custom-field">
                <span>From</span>
                <input
                  ref={customFromRef}
                  type="datetime-local"
                  defaultValue={customFrom}
                />
              </label>
              <label className="dash-custom-field">
                <span>To</span>
                <input
                  ref={customToRef}
                  type="datetime-local"
                  defaultValue={customTo}
                />
              </label>
              <div className="dash-custom-actions">
                <button type="button" className="dash-custom-apply" onClick={applyCustomRange}>
                  Apply
                </button>
                <button
                  type="button"
                  className="dash-custom-cancel"
                  onClick={() => { setCustomOpen(false); setCustomError(""); }}
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="dash-custom-hint">
              Maximum range is 90 days. Validation runs on Apply.
              {customError && <span className="dash-custom-error"> · {customError}</span>}
            </div>
          </div>
        )}
      </div>

      {/* ── KPI Cards ── */}
      <div className="dash-kpi-grid">
        <div className="dash-kpi">
          <div className="dash-kpi-icon kpi-blue"><FaEnvelopeOpen /></div>
          <div className="dash-kpi-body">
            <div className="dash-kpi-value">{(s.total_emails ?? 0).toLocaleString()}</div>
            <div className="dash-kpi-label">Emails Analyzed</div>
            <div className="dash-kpi-sub">in {activeRangeLabel.toLowerCase()}</div>
          </div>
        </div>

        <div className="dash-kpi">
          <div className="dash-kpi-icon kpi-green"><FaClock /></div>
          <div className="dash-kpi-body">
            <div className="dash-kpi-value">{(s.total_emails ?? 0) > 0 ? formatTimeSaved(s.total_time_saved_min) : "—"}</div>
            <div className="dash-kpi-label">Total Time Saved</div>
            <div className="dash-kpi-sub">vs {MANUAL_BASELINE_MIN} min manual avg</div>
          </div>
        </div>

        <div className="dash-kpi">
          <div className="dash-kpi-icon kpi-cyan"><FaChartBar /></div>
          <div className="dash-kpi-body">
            <div className="dash-kpi-value">{formatMs(s.avg_processing_ms)}</div>
            <div className="dash-kpi-label">Avg Processing Time</div>
            <div className="dash-kpi-sub">fastest: {formatMs(s.fastest_ms)}</div>
          </div>
        </div>

        <div className="dash-kpi">
          <div className="dash-kpi-icon kpi-red"><FaExclamationTriangle /></div>
          <div className="dash-kpi-body">
            <div className="dash-kpi-value">
              {(s.phishing_count ?? byVerdict.find((r) => r.verdict_class === "pill-danger")?.count) ?? 0}
            </div>
            <div className="dash-kpi-label">Phishing Detected</div>
            <div className="dash-kpi-sub">in {activeRangeLabel.toLowerCase()}</div>
          </div>
        </div>
      </div>

      {/* ── Middle row ── */}
      <div className="dash-mid-grid">
        {/* Verdict Breakdown */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3>Verdict Breakdown</h3>
            <span className="dash-card-meta">{(s.total_emails ?? 0).toLocaleString()} total</span>
          </div>
          {byVerdict.length === 0 ? (
            <div className="dash-empty">No data recorded yet</div>
          ) : (
            <div className="dash-verdict-list">
              {byVerdict.map((r) => (
                <div className="dash-verdict-row" key={r.verdict_class}>
                  <div className="dash-verdict-dot" style={{ background: verdictColor(r.verdict_class) }} />
                  <div className="dash-verdict-lbl">{verdictLabel(r.verdict_class)}</div>
                  <div className="dash-verdict-track">
                    <div
                      className="dash-verdict-fill"
                      style={{
                        width: `${(r.count / totalByVerdict) * 100}%`,
                        background: verdictColor(r.verdict_class),
                      }}
                    />
                  </div>
                  <div className="dash-verdict-count">{r.count}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Repeat Analyses */}
        <div className="dash-card dash-repeat-card">
          <div className="dash-card-header">
            <h3>
              <FaCopy style={{ marginRight: 6, opacity: 0.7 }} />Repeated submissions
              <span className="dash-card-sub">({activeRangeLabel})</span>
            </h3>
            <span className="dash-card-meta">{repeats.length} email{repeats.length !== 1 ? "s" : ""}</span>
          </div>
          {repeats.length === 0 ? (
            <div className="dash-empty">No emails analyzed more than once</div>
          ) : (
            <>
              <div className="dash-repeat-list">
                {repeatsSlice.map((r, idx) => {
                  const cls = dominantVerdictClass(r.verdict_classes);
                  const key = `${r.sender ?? ""}::${r.subject ?? ""}::${idx}`;
                  return (
                    <div className="dash-repeat-row" key={key}>
                      <div className="dash-repeat-name" title={r.subject || "(no subject)"}>
                        <span
                          className="dash-repeat-dot"
                          style={{ background: verdictColor(cls) }}
                        />
                        <span className="dash-repeat-subject">{r.subject || "(no subject)"}</span>
                      </div>
                      <span className="dash-repeat-count">{r.analysis_count}×</span>
                      <div className="dash-repeat-sender" title={r.sender || ""}>
                        {r.sender || "(unknown sender)"}
                      </div>
                      <div className="dash-repeat-meta">
                        <span>{formatShortDate(r.first_analyzed_at)} → {formatShortDate(r.last_analyzed_at)}</span>
                        <span className="dash-repeat-span">· {formatSpan(r.span_seconds)} span</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {repeatTotalPages > 1 && (
                <div className="dash-pager">
                  <button
                    className="dash-pager-btn"
                    onClick={() => setRepeatPage((p) => Math.max(0, p - 1))}
                    disabled={repeatPageSafe === 0}
                  >
                    Prev
                  </button>
                  <span className="dash-pager-info">
                    Page {repeatPageSafe + 1} of {repeatTotalPages}
                  </span>
                  <button
                    className="dash-pager-btn"
                    onClick={() => setRepeatPage((p) => Math.min(repeatTotalPages - 1, p + 1))}
                    disabled={repeatPageSafe >= repeatTotalPages - 1}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Top Senders / Recipients ── */}
      <div className="dash-top-grid">
        <div className="dash-card dash-top-card">
          <div className="dash-card-header">
            <h3>
              <FaPaperPlane style={{ marginRight: 6, opacity: 0.7 }} />Top 10 Senders
              <span className="dash-card-sub">({activeRangeLabel})</span>
            </h3>
            <span className="dash-card-meta">{topSenders.length} shown</span>
          </div>
          {topSenders.length === 0 ? (
            <div className="dash-empty">No senders recorded yet</div>
          ) : (
            <div className="dash-top-list">
              {topSenders.map((r, idx) => {
                const flagged = (r.phishing_count ?? 0) + (r.suspicious_count ?? 0);
                return (
                  <div className="dash-top-row" key={`s-${idx}-${r.sender}`}>
                    <div className="dash-top-rank">{idx + 1}</div>
                    <div className="dash-top-main">
                      <div className="dash-top-addr" title={r.sender}>{r.sender}</div>
                      <div className="dash-top-track">
                        <div
                          className="dash-top-fill dash-top-fill-sender"
                          style={{ width: `${(r.count / maxSenderCount) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div className="dash-top-stats">
                      <span className="dash-top-count">{r.count}</span>
                      {flagged > 0 && (
                        <span className="dash-top-flagged" title={`${r.phishing_count} phishing · ${r.suspicious_count} suspicious`}>
                          <FaExclamationTriangle /> {flagged}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="dash-card dash-top-card">
          <div className="dash-card-header">
            <h3>
              <FaInbox style={{ marginRight: 6, opacity: 0.7 }} />Top 10 Recipients
              <span className="dash-card-sub">({activeRangeLabel})</span>
            </h3>
            <span className="dash-card-meta">{topRecipients.length} shown</span>
          </div>
          {topRecipients.length === 0 ? (
            <div className="dash-empty">No recipients recorded yet</div>
          ) : (
            <div className="dash-top-list">
              {topRecipients.map((r, idx) => {
                const flagged = (r.phishing_count ?? 0) + (r.suspicious_count ?? 0);
                return (
                  <div className="dash-top-row" key={`r-${idx}-${r.recipient}`}>
                    <div className="dash-top-rank">{idx + 1}</div>
                    <div className="dash-top-main">
                      <div className="dash-top-addr" title={r.recipient}>{r.recipient}</div>
                      <div className="dash-top-track">
                        <div
                          className="dash-top-fill dash-top-fill-recipient"
                          style={{ width: `${(r.count / maxRecipientCount) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div className="dash-top-stats">
                      <span className="dash-top-count">{r.count}</span>
                      {flagged > 0 && (
                        <span className="dash-top-flagged" title={`${r.phishing_count} phishing · ${r.suspicious_count} suspicious`}>
                          <FaExclamationTriangle /> {flagged}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Recent Analyses Table ── */}
      <div className="dash-card dash-table-card">
        <div className="dash-card-header">
          <h3>Recent Submissions <span className="dash-card-sub">({activeRangeLabel})</span></h3>
          <div className="dash-card-header-right">
            <span className="dash-badge">{recent.length} in range</span>
          </div>
        </div>
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Analyzed At</th>
                <th>Mode</th>
                <th>Subject / Sender</th>
                <th>Verdict</th>
                <th>Processing</th>
                <th>Time Saved</th>
                <th>Analyst</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr>
                  <td colSpan={7} className="dash-table-empty">
                    <FaCheckCircle /> No analyses recorded yet
                  </td>
                </tr>
              ) : (
                recentSlice.map((r) => (
                  <tr key={r.id}>
                    <td className="dash-td-date">
                      {new Date(r.analyzed_at).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td>
                      <span className={`dash-mode-pill ${r.analysis_type === "ai" ? "mode-pill-ai" : "mode-pill-std"}`}>
                        {r.analysis_type === "ai" ? "AI" : "Standard"}
                      </span>
                    </td>
                    <td className="dash-td-email">
                      <div className="dash-td-subject" title={r.subject || "(no subject)"}>
                        {r.subject || "(no subject)"}
                      </div>
                      <div className="dash-td-sender" title={r.sender || ""}>
                        {r.sender || "(unknown sender)"}
                      </div>
                    </td>
                    <td>
                      <span
                        className="dash-verdict-chip"
                        style={{
                          background: `${verdictColor(r.verdict_class)}22`,
                          color: verdictColor(r.verdict_class),
                          border: `1px solid ${verdictColor(r.verdict_class)}55`,
                        }}
                      >
                        {verdictLabel(r.verdict_class)}
                      </span>
                    </td>
                    <td className="dash-td-mono">{formatMs(r.processing_time_ms)}</td>
                    <td className="dash-td-saved">
                      +{formatTimeSaved(r.time_saved_min)}
                    </td>
                    <td className="dash-td-analyst">{r.analyst_username || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {recentTotalPages > 1 && (
          <div className="dash-pager dash-pager-table">
            <button
              className="dash-pager-btn"
              onClick={() => setRecentPage((p) => Math.max(0, p - 1))}
              disabled={recentPageSafe === 0}
            >
              Prev
            </button>
            <span className="dash-pager-info">
              Page {recentPageSafe + 1} of {recentTotalPages} · showing {recentSlice.length} of {recent.length}
            </span>
            <button
              className="dash-pager-btn"
              onClick={() => setRecentPage((p) => Math.min(recentTotalPages - 1, p + 1))}
              disabled={recentPageSafe >= recentTotalPages - 1}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* ── Activity Chart (bottom) ── */}
      <div className="dash-card dash-chart-card">
        <div className="dash-card-header">
          <h3>Activity <span className="dash-card-sub">({activeRangeLabel})</span></h3>
          <span className="dash-card-meta">{(s.total_emails ?? 0).toLocaleString()} emails</span>
        </div>
        {byDay.length === 0 ? (
          <div className="dash-empty">No data recorded yet</div>
        ) : (
          <div className="dash-chart-area">
            <div className="dash-bars">
              {byDay.map((d) => (
                <div
                  className="dash-bar-col"
                  key={d.day}
                  title={`${d.day}: ${d.count} email${d.count !== 1 ? "s" : ""}`}
                >
                  <div className="dash-bar-val">{d.count > 0 ? d.count : ""}</div>
                  <div
                    className="dash-bar"
                    style={{ height: `${Math.max(4, (d.count / maxDay) * 100)}%` }}
                  />
                  <div className="dash-bar-label">{d.day.slice(5)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
